// mui
import {
  Box,
  Card,
  Divider,
  styled,
  Typography as TypographyBase,
} from '@mui/material';
// components
import { CurrencyAmount } from '@cowbell/ui/core';
import { Flexbox } from '../../../../../../components/layout/Flexbox';
import DescTooltip from '../../../../../../components/DescTooltip';
import { CancelledPolicyDrawerActions } from './CancelledPolicyDrawerActions';
// utils
import { formatCancellationPolicy } from '../policy-details.utils';
import TruncateTextWithTooltip from '../../../../../../utility/TruncateTextWithTooltip';

interface CancelledPolicySummaryPolicy {
  readonly totalPremium?: number | null;
  readonly refund?: number | null;
  /** ISO 4217 code supplied by the API alongside the policy. */
  readonly currency: string;
  readonly cancellationDescription?: string | null;
  readonly uiCancellationReason?: string | null;
  readonly cancellationDate?: string | null;
  readonly cancellationTriggerDate?: string | null;
}

interface CancelledPolicySummaryProps {
  readonly policy: CancelledPolicySummaryPolicy;
}

export const CancelledPolicySummary = ({
  policy,
}: CancelledPolicySummaryProps) => {
  const {
    totalPremium,
    refund,
    currency,
    cancellationDescription,
    uiCancellationReason,
  } = policy;

  const { uiCancellationDate, uiCancellationEffectiveDate } =
    formatCancellationPolicy(
      policy?.cancellationDate,
      policy?.cancellationTriggerDate
    );

  return (
    <>
      <PolicySummaryCard square>
        <Flexbox>
          <Typography variant="subtitle2" color="primary">
            Cancellation Information
          </Typography>
          <CancelledPolicyDrawerActions data={policy} />
        </Flexbox>
        <Divider style={{ width: '100%' }} />
        <Box>
          <Flexbox alignItems="flex-start">
            <Box
              width="50%"
              marginRight={1}
              style={{ borderRight: '.1rem solid' }}
            >
              <div style={{ paddingRight: '1rem' }}>
                <Flexbox>
                  <Typography variant="caption">Canceled On</Typography>
                  <Typography>{uiCancellationDate}</Typography>
                </Flexbox>
                <Flexbox>
                  <Typography>Cancellation Effective</Typography>
                  <Typography>{uiCancellationEffectiveDate}</Typography>
                </Flexbox>
                {/* @ts-expect-error TS(2741): Property 'children' is missing in type '{}' but re... Remove this comment to see the full error message */}
                <Flexbox />
              </div>
            </Box>
            <Box width="50%" marginLeft={1}>
              <Flexbox>
                <Typography>Cancellation Reason</Typography>
                <TruncateTextWithTooltip variant="caption">
                  {uiCancellationReason ?? '-'}
                </TruncateTextWithTooltip>
              </Flexbox>
              <Flexbox>
                <Typography>Total Premium</Typography>
                <Typography>
                  <CurrencyAmount
                  amount={totalPremium}
                  currency={currency}
                  currencySign="accounting"
                />
                </Typography>
              </Flexbox>
              <Flexbox>
                <Typography>
                  Premium Refund{' '}
                  <DescTooltip tooltip="Refund does not include any policy fee and is pro-rated for days of coverage provided." />
                </Typography>
                <Typography>
                  <CurrencyAmount amount={refund} currency={currency} />
                </Typography>
              </Flexbox>
            </Box>
          </Flexbox>
        </Box>
      </PolicySummaryCard>
      <PolicySummaryCard>
        <Divider style={{ width: '100%' }} />
        <Box>
          <Typography variant="subtitle2" color="primary">
            Cancellation Description
          </Typography>
        </Box>
        <Divider style={{ width: '100%' }} />
        <Box width="100%" marginRight={1}>
          <Flexbox>
            <Typography>
              {cancellationDescription ?? 'Description Unavailable'}
            </Typography>
          </Flexbox>
        </Box>
      </PolicySummaryCard>
    </>
  );
};

const Typography = ({ children, ...props }: any) => (
  <TypographyBase variant="caption" {...props}>
    {children}
  </TypographyBase>
);

const PolicySummaryCard = styled(Card)(({ theme: { palette } }) => ({
  padding: '1rem',
  backgroundColor: palette.background.default,
}));
